import { MePrFoService } from '@/services/meprfo';
import { OutboxLogic } from './logic';
import { loadOutbox, saveOutbox } from './storage';

/**
 * The query parameter that opens the outbox on a topic's send when the app
 * loads, for a sender coming back through their own leg link.
 */
export const OUTBOX_OPEN_PARAM = 'outbox';

/**
 * The sender tapping the link in a message they sent from the outbox — the way
 * back from Messages — rather than a recipient tapping it.
 *
 * Tapping the link of the message waiting on "did it send?" answers it: the
 * message went, so its leg is marked sent and the outbox moves on, the same as
 * confirming it in the list. A resend's leg was marked sent the first time.
 * If marking it fails, the message is left waiting and the outbox asks.
 *
 * Only that message's link is the way back. Any other link, including one to
 * a message already sent, opens the way a recipient's does, the same as a
 * sender re-reading their own link from the share wizard.
 *
 * Resolves the topic whose send the leg belongs to, for the caller to open
 * with the outbox over it, or null when no message in the outbox is waiting
 * on this leg.
 */
export async function returnThroughOutboxLeg(
  legId: string
): Promise<string | null> {
  const sends = await loadOutbox();
  const found = OutboxLogic.findLeg(sends, legId);
  if (!found) return null;
  const { send, index } = found;
  const item = send.items[index];
  if (item.state !== 'awaitingConfirm') return null;
  try {
    if (!item.resending) await MePrFoService.markLegAsSent(legId);
    const confirmed = OutboxLogic.confirm(send, index);
    await saveOutbox(sends.map(s => (s.id === send.id ? confirmed : s)));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      'Failed to confirm the text its leg link came back on:',
      error
    );
  }
  return send.topicId;
}
